/**
 * "Delete account" modal. Requires the user to:
 *   1. Re-enter their password (server-verified).
 *   2. Tick a confirmation checkbox.
 *
 * Cars owned by the user must be transferred or deleted first; we surface
 * a link to the garage up-front and, if the backend returns 409 owns_cars,
 * render the offending car ids inside the modal so the user can jump
 * straight to each one.
 */

import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

import { ApiError } from "../../api/client";
import { useDeleteAccount } from "../../api/account";
import Modal from "../../components/Modal";

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function DeleteAccountModal({
  isOpen,
  onClose,
}: DeleteAccountModalProps) {
  const del = useDeleteAccount();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ownedCarIds, setOwnedCarIds] = useState<number[] | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setPassword("");
      setConfirmed(false);
      setError(null);
      setOwnedCarIds(null);
    }
  }, [isOpen]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setOwnedCarIds(null);
    try {
      await del.mutateAsync({ current_password: password });
      navigate("/login", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("Password is incorrect.");
      } else if (err instanceof ApiError && err.status === 409) {
        const detail = err.rawDetail;
        if (typeof detail === "object" && detail) {
          const carIds = (detail as Record<string, unknown>).car_ids;
          if (Array.isArray(carIds)) {
            setOwnedCarIds(carIds as number[]);
          }
        }
        setError(
          "You still own one or more cars. Transfer or delete each owned car before deleting your account.",
        );
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Could not delete account.");
      }
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Delete account">
      <form className="account-form" onSubmit={handleSubmit}>
        <p className="section-help">
          This permanently removes your account and ends every active
          session. Cars you own will be deleted with you — to keep them,{" "}
          <Link to="/garage" onClick={onClose}>
            transfer them in the Garage
          </Link>{" "}
          first. Cars shared with you stay accessible to their owners.
        </p>

        <label className="form-row">
          <span className="form-row__label">Confirm with your password</span>
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="form-row__input"
            required
            autoFocus
          />
        </label>

        <label className="form-checkbox">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          />
          <span>I understand this cannot be undone.</span>
        </label>

        {error ? <p className="form-error">{error}</p> : null}
        {ownedCarIds && ownedCarIds.length > 0 ? (
          <p className="section-help">
            Owned cars to clear first:{" "}
            {ownedCarIds.map((id, i) => (
              <span key={id}>
                {i > 0 ? ", " : ""}
                <Link to={`/garage/${id}`} onClick={onClose}>
                  #{id}
                </Link>
              </span>
            ))}
          </p>
        ) : null}

        <div className="form-actions form-actions--inline">
          <button
            type="button"
            className="button button--ghost"
            onClick={onClose}
            disabled={del.isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="button button--danger"
            disabled={!confirmed || password.length === 0 || del.isPending}
          >
            {del.isPending ? "Deleting…" : "Delete my account"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
